import { apiClient } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { ToastType } from '@components/Toast';
import type { ChangeTrackingSource } from '@typeDefinitions/changeTrackingTypes';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';

import {
  GroupCreatePayload,
  PostingGroupData,
  PostingGroupIdTemplateIdXrefDto,
  PostingGroupTemplatesT,
  PostingUsersTypes,
} from './types';

export const fetchPostingGroupApi = async (officeId: string) => {
  try {
    const res: V4ApiResponse<PostingGroupData[]> = await apiClient.get(
      `/PostingGroups/${officeId}`,
    );
    if (res.isSuccess) {
      return res.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to Fetch PostingGroup');
    handleToast(ToastType.Error, error.message);
  }
};

export const postingUsersApi = async (officeId: string) => {
  try {
    const res: V4ApiResponse<PostingUsersTypes[]> = await apiClient.get(
      `PostingGroups/PostingUser/${officeId}`,
    );
    if (res.isSuccess) {
      return res.value;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to Fetch PostingUsers');
    handleToast(ToastType.Error, error.message);
  }
};

export const createPostingGroupApi = async (
  payload: GroupCreatePayload,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: {
      isSuccess: boolean;
      value: PostingGroupIdTemplateIdXrefDto;
    } = await apiClient.post(
      '/PostingGroups',
      payload,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to Create PostingGroup');
    handleToast(ToastType.Error, error.message);
  }
};

export const editPostingGroupApi = async (
  payload: PostingGroupData,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<PostingGroupData> = await apiClient.put(
      '/PostingGroups',
      payload,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    if (response.isSuccess) {
      return response;
    }
  } catch (e: unknown) {
    const error = createError(e, 'Failed to Edit PostingGroup');
    handleToast(ToastType.Error, error.message);
  }
};

export const deletePostingGroupApi = async (
  payload: PostingGroupData,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<null> = await apiClient.delete(
      `/PostingGroups/${payload.id}`,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to Delete PostingGroup');
    handleToast(ToastType.Error, error.message);
  }
};

export const editPostingUserApi = async (
  payload: PostingUsersTypes,
  changeTrackingSource?: ChangeTrackingSource,
): Promise<V4ApiResponse<PostingUsersTypes> | undefined> => {
  try {
    const response: V4ApiResponse<PostingUsersTypes> = await apiClient.put(
      'PostingGroups/postinguser',
      payload,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );

    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to Edit Posting User');
    handleToast(ToastType.Error, error.message);
  }
};

export const deletePostingUserApi = async (
  payload: PostingUsersTypes,
  changeTrackingSource?: ChangeTrackingSource,
) => {
  try {
    const response: V4ApiResponse<boolean> = await apiClient.delete(
      `/PostingGroups/postinguser/${payload.id}`,
      changeTrackingSource
        ? buildChangeTrackingRequestConfig([changeTrackingSource])
        : undefined,
    );
    return response;
  } catch (e: unknown) {
    const error = createError(e, 'Failed to Delete User From Posting Group');
    handleToast(ToastType.Error, error.message);
  }
};

export const getOverrideTemplates = async () => {
  try {
    const resp: V4ApiResponse<PostingGroupTemplatesT> = await apiClient.get(
      '/PostingGroups/overridetemplates',
    );

    if (!resp.isSuccess) {
      throw resp.errors;
    }

    return resp.value;
  } catch (e) {
    throw createError(e, 'Failed to get override templates');
  }
};
